import * as React from 'react';
import loadScript from 'load-script2';

function _inheritsLoose(subClass, superClass) {
  subClass.prototype = Object.create(superClass.prototype);
  subClass.prototype.constructor = subClass;

  _setPrototypeOf(subClass, superClass);
}

function _setPrototypeOf(o, p) {
  _setPrototypeOf = Object.setPrototypeOf || function _setPrototypeOf(o, p) {
    o.__proto__ = p;
    return o;
  };

  return _setPrototypeOf(o, p);
}

function _assertThisInitialized(self) {
  if (self === void 0) {
    throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
  }

  return self;
}

var eventNames = ['onReady', 'onStateChange', 'onPlaybackQualityChange', 'onPlaybackRateChange', 'onError', 'onApiChange'];
/* global window */

function loadSdk() {
  return new Promise(function (resolve, reject) {
    if (typeof window.YT === 'object' && typeof window.YT.ready === 'function') {
      // A YouTube SDK is already loaded, so reuse that
      window.YT.ready(function () {
        resolve(window.YT);
      });
      return;
    }

    loadScript('https://www.youtube.com/iframe_api', function (err) {
      if (err) {
        reject(err);
      } else {
        window.YT.ready(function () {
          resolve(window.YT);
        });
      }
    });
  });
}

var sdk = null;

function getSdk() {
  if (!sdk) {
    sdk = loadSdk();
  }

  return sdk;
}

var YouTube = /*#__PURE__*/function (_React$Component) {
  _inheritsLoose(YouTube, _React$Component);

  function YouTube(props) {
    var _this;

    _this = _React$Component.call(this, props) || this;
    _this.onPlayerReady = _this.onPlayerReady.bind(_assertThisInitialized(_this));
    _this.onPlayerStateChange = _this.onPlayerStateChange.bind(_assertThisInitialized(_this));
    _this.refContainer = _this.refContainer.bind(_assertThisInitialized(_this));
    return _this;
  }

  var _proto = YouTube.prototype;

  _proto.componentDidMount = function componentDidMount() {
    this.createPlayer();
  };

  _proto.componentDidUpdate = function componentDidUpdate(prevProps) {
    var _this2 = this; // eslint-disable-next-line react/destructuring-assignment


    var changes = Object.keys(this.props).filter(function (name) {
      return _this2.props[name] !== prevProps[name];
    });
    this.updateProps(changes);
  };

  _proto.componentWillUnmount = function componentWillUnmount() {
    if (this.playerInstance) {
      this.playerInstance.destroy();
    }
  };

  _proto.onPlayerReady = function onPlayerReady(event) {
    var _this$props = this.props,
        volume = _this$props.volume,
        muted = _this$props.muted,
        suggestedQuality = _this$props.suggestedQuality,
        playbackRate = _this$props.playbackRate;

    if (typeof volume !== 'undefined') {
      event.target.setVolume(volume * 100);
    }

    if (typeof muted !== 'undefined') {
      if (muted) {
        event.target.mute();
      } else {
        event.target.unMute();
      }
    }

    if (typeof suggestedQuality !== 'undefined') {
      event.target.setPlaybackQuality(suggestedQuality);
    }

    if (typeof playbackRate !== 'undefined') {
      event.target.setPlaybackRate(playbackRate);
    }

    this.resolvePlayer(event.target);
  };

  _proto.onPlayerStateChange = function onPlayerStateChange(event) {
    var _this$props2 = this.props,
        onCued = _this$props2.onCued,
        onBuffering = _this$props2.onBuffering,
        onPause = _this$props2.onPause,
        onPlaying = _this$props2.onPlaying,
        onEnd = _this$props2.onEnd;
    var State = YT.PlayerState; // eslint-disable-line no-undef

    switch (event.data) {
      case State.CUED:
        onCued(event);
        break;

      case State.BUFFERING:
        onBuffering(event);
        break;

      case State.PAUSED:
        onPause(event);
        break;

      case State.PLAYING:
        onPlaying(event);
        break;

      case State.ENDED:
        onEnd(event);
        break;
    }
  }
  /**
   * @private
   */
  ;

  _proto.getPlayerParameters = function getPlayerParameters() {
    /* eslint-disable react/destructuring-assignment */
    return {
      autoplay: this.props.autoplay,
      cc_load_policy: this.props.showCaptions ? 1 : 0,
      controls: this.props.controls ? 1 : 0,
      disablekb: this.props.disableKeyboard ? 1 : 0,
      fs: this.props.allowFullscreen ? 1 : 0,
      hl: this.props.lang,
      iv_load_policy: this.props.annotations ? 1 : 3,
      start: this.props.startSeconds,
      end: this.props.endSeconds,
      modestbranding: this.props.modestBranding ? 1 : 0,
      playsinline: this.props.playsInline ? 1 : 0,
      rel: this.props.showRelatedVideos ? 1 : 0
    };
    /* eslint-enable react/destructuring-assignment */
  }
  /**
   * @private
   */
  ;

  _proto.getInitialOptions = function getInitialOptions() {
    /* eslint-disable react/destructuring-assignment */
    return {
      videoId: this.props.video,
      width: this.props.width,
      height: this.props.height,
      playerVars: this.getPlayerParameters(),
      events: {
        onReady: this.onPlayerReady,
        onStateChange: this.onPlayerStateChange
      }
    };
    /* eslint-enable react/destructuring-assignment */
  }
  /**
   * @private
   */
  ;

  _proto.updateProps = function updateProps(propNames) {
    var _this3 = this;

    this.player.then(function (player) {
      propNames.forEach(function (name) {
        // eslint-disable-next-line react/destructuring-assignment
        var value = _this3.props[name];

        switch (name) {
          case 'muted':
            if (value) {
              player.mute();
            } else {
              player.unMute();
            }

            break;

          case 'suggestedQuality':
            player.setPlaybackQuality(value);
            break;

          case 'volume':
            player.setVolume(value * 100);
            break;

          case 'paused':
            if (value && player.getPlayerState() !== 2) {
              player.pauseVideo();
            } else if (!value && player.getPlayerState() === 2) {
              player.playVideo();
            }

            break;

          case 'id':
          case 'className':
          case 'width':
          case 'height':
            player.getIframe()[name] = value; // eslint-disable-line no-param-reassign

            break;

          case 'video':
            if (!value) {
              player.stopVideo();
            } else {
              var _this3$props = _this3.props,
                  startSeconds = _this3$props.startSeconds,
                  endSeconds = _this3$props.endSeconds,
                  autoplay = _this3$props.autoplay;
              var opts = {
                videoId: value,
                startSeconds: startSeconds || 0,
                endSeconds: endSeconds
              };

              if (autoplay) {
                player.loadVideoById(opts);
              } else {
                player.cueVideoById(opts);
              }
            }

            break;
        }
      });
    });
  }
  /**
   * @private
   */
  ;

  _proto.createPlayer = function createPlayer() {
    var _this4 = this;

    var volume = this.props.volume;
    this.player = getSdk().then(function (YT) {
      return new Promise(function (resolve) {
        _this4.resolvePlayer = resolve;
        var player = new YT.Player(_this4.container, _this4.getInitialOptions()); // Store the instance directly so we can destroy it sync in
        // `componentWillUnmount`.

        _this4.playerInstance = player;
        eventNames.forEach(function (name) {
          player.addEventListener(name, function (event) {
            // eslint-disable-next-line react/destructuring-assignment
            var handler = _this4.props[name];

            if (handler) {
              handler(event);
            }
          });
        });
      });
    });

    if (typeof volume === 'number') {
      this.updateProps(['volume']);
    }
  }
  /**
   * @private
   */
  ;

  _proto.refContainer = function refContainer(container) {
    this.container = container;
  };

  _proto.render = function render() {
    var _this$props3 = this.props,
        id = _this$props3.id,
        className = _this$props3.className,
        style = _this$props3.style;
    return /*#__PURE__*/React.createElement("div", {
      id: id,
      className: className,
      style: style,
      ref: this.refContainer
    });
  };

  return YouTube;
}(React.Component);

if (process.env.NODE_ENV !== 'production') {}

YouTube.defaultProps = {
  autoplay: false,
  showCaptions: false,
  controls: true,
  disableKeyboard: false,
  allowFullscreen: true,
  annotations: true,
  modestBranding: false,
  playsInline: false,
  showRelatedVideos: true,
  onCued: function onCued() {},
  onBuffering: function onBuffering() {},
  onPlaying: function onPlaying() {},
  onPause: function onPause() {},
  onEnd: function onEnd() {}
};
export { YouTube as default };